import { Injectable } from '@angular/core';
import { ApiEntityService } from '@core/api/api-entity.service';
import { Automapper } from '@core/api/mapper';
import { DataroomNode } from '@core/models/dataroom-node.model';
import { removeDuplicates } from '@core/utils';
import { HttpGetConfig } from '@datorama/akita-ng-entity-service';
import { Observable } from 'rxjs';
import { take, tap } from 'rxjs/operators';

import { DataroomState, DataroomStore } from './dataroom.store';
import { UserDataroomResourceBaseUrlBuilder } from './user-dataroom-resource-base-url-builder.service';

@Injectable({ providedIn: 'root' })
export class UserDataroomService extends ApiEntityService<DataroomState> {
  constructor(protected store: DataroomStore, protected urlBuilder: UserDataroomResourceBaseUrlBuilder) {
    super(store, urlBuilder, Automapper.create(DataroomNode));
    this.overrideResourceName('data-room');
  }

  public setLoading(loading: boolean): void {
    this.store.setLoading(loading);
  }

  public getNode(id: string): Observable<DataroomNode> {
    return this.useResourceSuffix('nodes')
      .get<DataroomNode>(id, { skipWrite: true })
      .pipe(
        take(1),
        tap(() => {
          this.setLoading(false);
        }),
      );
  }

  public getNodes(parent: string): Observable<DataroomNode[]> {
    const httpConfig: HttpGetConfig = {
      params: parent
        ? {
            parent,
          }
        : null,
      skipWrite: true,
    };
    return this.useResourceSuffix('nodes')
      .get<DataroomNode[]>({}, httpConfig)
      .pipe(
        take(1),
        tap((nodes) => {
          this.setLoading(false);
          this.updateAvailableNodes(nodes);
        }),
      );
  }

  public updateAvailableNodes(nodes: DataroomNode[]): void {
    this.store.update((state) => {
      const uniqueNodes = [...(state.nodes || []), ...nodes];
      return {
        ...state,
        nodes: removeDuplicates(uniqueNodes, (x, y) => x.id === y.id),
      };
    });
  }

  public resetAvailableNodes(): void {
    this.store.update((state) => {
      return {
        ...state,
        nodes: null,
      };
    });
  }
}
